/* =========================================================================
   LandingPage8 — the offer-plan revision of LandingPage7.

   LandingPage7 is UNTOUCHED and still serves `/` and `/landing-page7`; this is
   a parallel page on `/landing-page8` so the two can be compared and either can
   be promoted to home with a one-line change in App.js.

   Structure still follows $100M Offers, but five things changed after the offer
   review, and each one is a deliberate answer to something the old page got
   wrong:

     1. THE HERO LEADS WITH THE DAY-ONE WIN. V4102 puts the student's own
        branded app on screen in the second lesson, and LandingPage7 never
        mentioned it — it sold "ship in 60 days" alone. The most expensive
        belief to overcome is "someone like me can't do this," and the course
        disproves it in about twenty minutes. Time-delay is the denominator
        term this page can most cheaply attack.

     2. A NEW PROOF SECTION (.lp8-proof). The old page sold the dream outcome
        hard and its BELIEVABILITY not at all. These five are real shipped
        mechanisms, not claims: automatic grading, the Spanish tutor, narration
        on all 43 lessons, a course that ends at a deployed URL, and a free
        first lesson that is itself the app-idea lesson.

     3. THE COUNT IS 43, NOT 30. The lp6 keys still say "30 lessons across 5
        modules" — left over from CD01 — which undersells the finished CD04 by
        thirteen lessons. Not fixed in lp6 on purpose: those keys are shared
        with LandingPage7, LandingPage6 and Subscriptions3, and this page was
        asked to leave them alone.

     4. THE PRICE CARD LEADS WITH 6 × $97. For an avatar whose constraint is
        cash flow rather than willingness, the monthly figure is the one that
        decides the sale; $497 stays as the cheaper pay-in-full option. The
        anchorPunch line ("hire an engineer and you pay every time you have an
        idea") moves up beside the price, where it was previously stranded at
        the bottom of the comparison block.

     5. THE GUARANTEE SAYS THE CONDITIONS ARE MACHINE-CHECKED. Progress is
        tracked per lesson and the finish line is a deployed URL, so the terms
        can be stated with a precision competitors can't match — which is also
        what stops the guarantee reading as designed-to-be-unclaimable.

   Three bonuses (.lp8-bonus) name the walls the course itself does not remove:
   the paid Claude plan Module 4 needs (gifted at Module 4, not at purchase, so
   seats aren't bought for students who stopped at lesson five), the deploy step
   (a 1-on-1, positioned as the finish-line reward rather than as tech support),
   and "what now" once the app is live.

   Copy lives under lp8.* in both locales — Spanish is written, not falling back
   to English, since that is the primary audience. Everything is scoped under
   .lp8 so it never collides with the Argon globals or any earlier landing page.
   ========================================================================= */

import React, { useEffect } from "react";

// reactstrap components
import { Container, Row, Col } from "reactstrap";

import "./LandingPage8.css";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLaptopCode,
  faDollarSign,
  faLanguage,
  faUsers,
  faLightbulb,
  faRobot,
  faRocket,
  faCheck,
  faShieldHalved,
  faClock,
  faGraduationCap,
  faUserTie,
  faInfinity,
  faXmark,
  faCircleCheck,
  faComments,
  faHeadphones,
  faLink,
  faUnlockKeyhole,
  faGift,
  faTowerBroadcast,
  faBriefcase,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { ENROLLMENT_OPEN } from "../../config/enrollment";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// The fears that keep the idea stuck (Hormozi step 2: list the problems).
const PROBLEMS = [
  { n: 1, icon: faLaptopCode, tone: "lp8-ic-cyan" },
  { n: 2, icon: faDollarSign, tone: "lp8-ic-purple" },
  { n: 3, icon: faLanguage, tone: "lp8-ic-blue" },
  { n: 4, icon: faUsers, tone: "lp8-ic-cyan" },
];

// Why finishing is the likely outcome and not the lucky one. Every item maps to
// something that actually ships in CD04 — nothing here is a promise the product
// doesn't already keep.
const PROOF = [
  { n: 1, icon: faCircleCheck },
  { n: 2, icon: faComments, alt: true },
  { n: 3, icon: faHeadphones },
  { n: 4, icon: faLink, alt: true },
  { n: 5, icon: faUnlockKeyhole },
];

// How it works — the believable path to the dream outcome. Step 2 is the
// day-one win the hero now opens on.
const STEPS = [
  { n: 1, icon: faLightbulb, tone: "lp8-ic-cyan" },
  { n: 2, icon: faWandMagicSparkles, tone: "lp8-ic-purple" },
  { n: 3, icon: faRobot, tone: "lp8-ic-blue" },
  { n: 4, icon: faRocket, tone: "lp8-ic-cyan" },
];

// Each bonus removes one wall the course itself leaves standing.
const BONUSES = [
  { n: 1, icon: faGift },
  { n: 2, icon: faTowerBroadcast },
  { n: 3, icon: faBriefcase },
];

// The "everything you get" list. Written inline rather than through the shared
// ValueStack component, which is hard-wired to the lp6 keys that LandingPage7
// and Subscriptions3 also read — and those still say "30 lessons".
const INCLUDES = [1, 2, 3, 4, 5, 6, 7, 8];

const LandingPage8 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Transparent navbar over the hero, fading to solid navy on scroll. Body
  // classes are cleaned up on unmount so every other page keeps the solid one.
  useEffect(() => {
    document.body.classList.add("lp8-active");
    const onScroll = () => {
      document.body.classList.toggle("lp8-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("lp8-active", "lp8-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const goEnroll = () => navigate("/subscriptions");
  // The hero's primary action opens the free first lesson, not the checkout.
  // V4101 is public and is the lesson where the student names what they're
  // building — so the free preview ends with their own project already captured,
  // which is a better first step than a price they haven't been sold on yet.
  const goFree = () => navigate("/claudedev-v4-table");
  const scrollToOffer = () => {
    const el = document.getElementById("lp8-offer");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <DemoNavbar />
      <main className="lp8">
        {/* ----------------------------- HERO ----------------------------- */}
        <section className="lp8-hero">
          <span className="lp8-orb lp8-orb-1" />
          <span className="lp8-orb lp8-orb-2" />
          <span className="lp8-orb lp8-orb-3" />

          <Container>
            <Row>
              <Col lg="7">
                <div className="lp8-glass">
                  <span className="lp8-eyebrow">
                    <span className="dot" />
                    {t("lp8.eyebrow")}
                  </span>
                  <h1 className="lp8-title">{t("lp8.heroTitle")}</h1>
                  <p className="lp8-lead">{t("lp8.heroLead")}</p>

                  <div className="lp8-actions">
                    <button className="lp8-btn-primary" onClick={goFree}>
                      {t("lp8.heroPrimary")}
                    </button>
                    <button className="lp8-btn-ghost" onClick={scrollToOffer}>
                      {t("lp8.heroSecondary")}
                    </button>
                  </div>

                  <div className="lp8-pills">
                    <span className="lp8-pill">{t("lp8.pill1")}</span>
                    <span className="lp8-pill">{t("lp8.pill2")}</span>
                    <span className="lp8-pill">{t("lp8.pill3")}</span>
                  </div>
                </div>
              </Col>

              <Col lg="5">
                <div className="lp8-preview-wrap">
                  <div className="lp8-preview">
                    <div className="lp8-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="lp8-code">
                      <div className="lp8-code-line">
                        <div className="lp8-bar cyan" style={{ width: "34%" }} />
                        <div className="lp8-bar mute" style={{ width: "24%" }} />
                      </div>
                      <div className="lp8-code-line">
                        <div className="lp8-bar purple" style={{ width: "20%" }} />
                        <div className="lp8-bar mute" style={{ width: "44%" }} />
                      </div>
                      <div className="lp8-code-line">
                        <div className="lp8-bar dim" style={{ width: "56%" }} />
                      </div>
                    </div>
                    <div className="lp8-bubble">
                      <div className="lp8-avatar" />
                      <div className="lp8-bubble-lines">
                        <div className="lp8-bar" style={{ width: "90%" }} />
                        <div className="lp8-bar" style={{ width: "68%" }} />
                      </div>
                    </div>
                    <span className="lp8-ask">{t("lp8.previewAsk")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- PROBLEM ---------------------------- */}
        <section className="lp8-problem">
          <Container>
            <div className="lp8-section-head lp8-head-dark">
              <h2>{t("lp8.problemTitle")}</h2>
              <p>{t("lp8.problemSubtitle")}</p>
            </div>
            <Row>
              {PROBLEMS.map((p) => (
                <Col lg="3" md="6" className="lp8-col" key={p.n}>
                  <div className="lp8-card lp8-card-dark">
                    <div className={`lp8-card-icon ${p.tone}`}>
                      <FontAwesomeIcon icon={p.icon} />
                    </div>
                    <h6>{t(`lp8.problem${p.n}T`)}</h6>
                    <p>{t(`lp8.problem${p.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ----------------------------- PROOF ---------------------------- */}
        {/* Answers the objection the section above just raised — "you've quit
            before" — with the mechanisms that make this attempt different. */}
        <section className="lp8-proof">
          <Container>
            <div className="lp8-section-head">
              <span className="lp8-eyebrow-2">{t("lp8.proofEyebrow")}</span>
              <h2>{t("lp8.proofTitle")}</h2>
              <p>{t("lp8.proofSubtitle")}</p>
            </div>

            <Row className="lp8-proof-grid">
              {PROOF.map((p) => (
                <Col lg="4" md="6" className="lp8-col" key={p.n}>
                  <div className="lp8-proof-item">
                    <div className={`lp8-proof-ic${p.alt ? " alt" : ""}`}>
                      <FontAwesomeIcon icon={p.icon} />
                    </div>
                    <div>
                      <h6>{t(`lp8.proof${p.n}T`)}</h6>
                      <p>{t(`lp8.proof${p.n}P`)}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ------------------------- HOW IT WORKS ------------------------- */}
        <section className="lp8-how">
          <Container>
            <div className="lp8-section-head">
              <h2>{t("lp8.howTitle")}</h2>
              <p>{t("lp8.howSubtitle")}</p>
            </div>
            <Row>
              {STEPS.map((s, i) => (
                <Col lg="3" md="6" className="lp8-col" key={s.n}>
                  <div className="lp8-step">
                    <span className="lp8-step-num">{i + 1}</span>
                    <div className={`lp8-card-icon ${s.tone}`}>
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                    <h6>{t(`lp8.how${s.n}T`)}</h6>
                    <p>{t(`lp8.how${s.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* --------------------------- THE ANCHOR ------------------------- */}
        {/* Hormozi price anchor: frame the cost of the real alternatives
            (college / hiring an engineer) right before the offer. Deliberately
            NOT a value stack — the comparison uses costs the buyer already
            believes instead of self-assigned line-item values. */}
        <section className="lp8-anchor">
          <Container>
            <div className="lp8-section-head lp8-head-dark">
              <span className="lp8-eyebrow-2 lp8-eyebrow-light">
                {t("lp8.anchorEyebrow")}
              </span>
              <h2>{t("lp8.anchorTitle")}</h2>
              <p>{t("lp8.anchorSubtitle")}</p>
            </div>

            <Row className="lp8-anchor-grid">
              <Col lg="4" md="6" className="lp8-col">
                <div className="lp8-anchor-card">
                  <span className="lp8-anchor-mark cross">
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                  <div className="lp8-anchor-ic">
                    <FontAwesomeIcon icon={faGraduationCap} />
                  </div>
                  <h6>{t("lp8.anchorCollegeLabel")}</h6>
                  <div className="lp8-anchor-cost">
                    {t("lp8.anchorCollegeCost")}
                  </div>
                  <p>{t("lp8.anchorCollegeNote")}</p>
                </div>
              </Col>

              <Col lg="4" md="6" className="lp8-col">
                <div className="lp8-anchor-card">
                  <span className="lp8-anchor-mark cross">
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                  <div className="lp8-anchor-ic">
                    <FontAwesomeIcon icon={faUserTie} />
                  </div>
                  <h6>{t("lp8.anchorEngineerLabel")}</h6>
                  <div className="lp8-anchor-cost">
                    {t("lp8.anchorEngineerCost")}
                  </div>
                  <p>{t("lp8.anchorEngineerNote")}</p>
                </div>
              </Col>

              <Col lg="4" md="6" className="lp8-col">
                <div className="lp8-anchor-card win">
                  <span className="lp8-anchor-mark check">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <div className="lp8-anchor-ic">
                    <FontAwesomeIcon icon={faInfinity} />
                  </div>
                  <h6>{t("lp8.anchorYouLabel")}</h6>
                  <div className="lp8-anchor-cost">
                    {t("lp8.anchorYouCost")}
                  </div>
                  <p>{t("lp8.anchorYouNote")}</p>
                </div>
              </Col>
            </Row>

            <p className="lp8-anchor-punch">{t("lp8.anchorPunch")}</p>
          </Container>
        </section>

        {/* --------------------- OFFER / WHAT'S INCLUDED ------------------- */}
        <section className="lp8-offer" id="lp8-offer">
          <Container>
            <div className="lp8-section-head">
              <span className="lp8-eyebrow-2">{t("lp8.includesEyebrow")}</span>
              <h2>{t("lp8.includesTitle")}</h2>
              <p>{t("lp8.includesSubtitle")}</p>
            </div>

            <Row className="lp8-offer-grid">
              <Col lg="7">
                <div className="lp8-stack">
                  {INCLUDES.map((n) => (
                    <div className="lp8-stack-row" key={n}>
                      <span className="lp8-stack-check">
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                      <span className="lp8-stack-name">{t(`lp8.inc${n}`)}</span>
                    </div>
                  ))}
                </div>
              </Col>

              <Col lg="5">
                <div className="lp8-price-card">
                  <span className="lp8-price-eyebrow">
                    {t("lp8.priceEyebrow")}
                  </span>
                  <h3 className="lp8-price-title">{t("lp8.priceTitle")}</h3>
                  <p className="lp8-price-sub">{t("lp8.priceSubtitle")}</p>

                  {/* Plan-led: the monthly number is the one that decides the
                      sale for a buyer whose constraint is cash flow, not
                      willingness. Pay-in-full sits underneath as the saving. */}
                  <div className="lp8-price-amount plan">
                    <span className="lp8-price-now">
                      {t("lp8.priceAmount")}
                    </span>
                    <span className="lp8-price-permonth">
                      {t("lp8.priceAmountNote")}
                    </span>
                  </div>
                  <span className="lp8-price-full">{t("lp8.priceFull")}</span>
                  <span className="lp8-price-punch">{t("lp8.anchorPunch")}</span>

                  <button
                    className="lp8-btn-primary lp8-price-btn"
                    onClick={goEnroll}
                  >
                    {/* Don't promise a seat we can't sell yet — while enrollment
                        is closed this reads "Coming soon" and the offer page it
                        opens explains why. */}
                    {ENROLLMENT_OPEN
                      ? t("lp8.priceCta")
                      : t("enrollment.comingSoonCta")}
                  </button>

                  <div className="lp8-price-meta">
                    <span>
                      <FontAwesomeIcon icon={faUsers} /> {t("lp8.priceSeats")}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faClock} /> {t("lp8.priceStart")}
                    </span>
                  </div>

                  <div className="lp8-price-guarantee">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    <span>{t("lp8.guaranteeTitle")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- BONUSES ---------------------------- */}
        {/* One per wall the course leaves standing: the paid plan Module 4
            needs, the deploy step, and "what now" once the app is live. */}
        <section className="lp8-bonus">
          <Container>
            <div className="lp8-section-head">
              <span className="lp8-eyebrow-2">{t("lp8.bonusEyebrow")}</span>
              <h2>{t("lp8.bonusTitle")}</h2>
            </div>
            <Row>
              {BONUSES.map((b) => (
                <Col lg="4" md="6" className="lp8-col" key={b.n}>
                  <div className="lp8-bonus-card">
                    <span className="lp8-bonus-tag">
                      {t("lp8.bonusTag")} {b.n}
                    </span>
                    <div className="lp8-bonus-ic">
                      <FontAwesomeIcon icon={b.icon} />
                    </div>
                    <h6>{t(`lp8.bonus${b.n}T`)}</h6>
                    <p>{t(`lp8.bonus${b.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* --------------------------- GUARANTEE -------------------------- */}
        <section className="lp8-guarantee">
          <Container>
            <div className="lp8-guarantee-card">
              <div className="lp8-guarantee-badge">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <span className="lp8-eyebrow-2">
                  {t("lp8.guaranteeEyebrow")}
                </span>
                <h3>{t("lp8.guaranteeTitle")}</h3>
                <p>{t("lp8.guaranteeBody")}</p>
                {/* The line that stops the guarantee reading as designed to be
                    unclaimable: the conditions are checked by the platform. */}
                <span className="lp8-guarantee-check">
                  <FontAwesomeIcon icon={faCircleCheck} />
                  {t("lp8.guaranteeCheck")}
                </span>
              </div>
            </div>
          </Container>
        </section>

        {/* --------------------------- FINAL CTA -------------------------- */}
        <section className="lp8-cta">
          <Container>
            <div className="lp8-cta-card">
              <Row>
                <Col lg="8">
                  <h3>{t("lp8.finalTitle")}</h3>
                  <p>{t("lp8.finalBody")}</p>
                </Col>
                <Col lg="4" className="ml-lg-auto">
                  <button className="lp8-cta-btn" onClick={goFree}>
                    {t("lp8.finalCta")}
                  </button>
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default LandingPage8;
